import React, { useState, useEffect } from 'react';
import { Phone, CheckCircle, Clock, Building2, PhoneForwarded, Eye, Bot, Hash, Play, Download, XCircle, Headphones } from 'lucide-react';
import { transfersService } from 'api/services/transfers.service';
import { organizationsService } from 'api/services/organizations.service';
import { callsService } from 'api/services/calls.service';
import { recordingsService } from 'api/services/recordings.service';
import { CallTransfer, CallLog, PaginatedResponse, FilterParams } from 'types';
import { useApi } from 'hooks/useApi';
import { useAuth } from 'hooks/useAuth';
import { LoadingSpinner } from 'components/Common/LoadingSpinner';
import { ErrorMessage } from 'components/Common/ErrorMessage';
import Pagination from 'components/Common/Pagination';
import TransfersFilterBar from 'components/Filters/TransfersFilterBar';
import OrganizationFilter from 'components/Filters/OrganizationFilter';
import AudioPlayer from 'components/Common/AudioPlayer';
import { formatTimestamp, getDefaultDateRange, maskSensitiveNumbers } from 'utils/helpers';
import { ITEMS_PER_PAGE, FEATURE_FLAGS } from 'utils/constants';

/**
 * The switch leaves `transfer_type` and `transfer_status` null on every row it
 * federates, so these render an explicit "—" rather than throwing. Showing the
 * gap is the honest option: defaulting to "ai" or "hotkey" would invent a fact
 * about a real call.
 */
const typeLabel = (t: string | null | undefined) => (t ? t.toUpperCase() : '—');
const statusOf = (s: string | null | undefined) => (s || '').toLowerCase();
const isDone = (s: string | null | undefined) =>
  statusOf(s) === 'answer' || statusOf(s) === 'completed';

const Transfers: React.FC = () => {
  const { user, currentOrganization } = useAuth();
  const currentRole = (currentOrganization?.role || user?.role)?.toLowerCase();

  const [currentPage, setCurrentPage] = useState(1);
  // Feature Flag: REQUIRE_ORG_SELECTION_FOR_OPERATIONAL_DATA
  // When enabled, defaults to current organization ID, otherwise 'all'
  const [selectedOrgId, setSelectedOrgId] = useState<string | 'all'>(
    FEATURE_FLAGS.REQUIRE_ORG_SELECTION_FOR_OPERATIONAL_DATA
      ? (currentOrganization?.id || '')
      : 'all'
  );
  const [filters, setFilters] = useState<FilterParams>({
    perPage: ITEMS_PER_PAGE,
    ...getDefaultDateRange() // Add default 2-day filter (configurable via DEFAULT_DATE_RANGE_DAYS)
  });
  const [selectedTransfer, setSelectedTransfer] = useState<CallTransfer | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [callLog, setCallLog] = useState<CallLog | null>(null);
  const [loadingCallLog, setLoadingCallLog] = useState(false);
  const [showPlayer, setShowPlayer] = useState(false);
  const [authenticatedUrl, setAuthenticatedUrl] = useState<string>('');

  // Determine organizationId to pass to API
  const getOrganizationId = () => {
    if (currentRole === 'master_admin' || currentRole === 'super_admin' || currentRole === 'service_admin') {
      // When REQUIRE_ORG_SELECTION is enabled, selectedOrgId should never be 'all' or empty
      // Return undefined for 'all' or empty string to prevent invalid API calls
      if (selectedOrgId === 'all' || !selectedOrgId) {
        return undefined;
      }
      return selectedOrgId;
    }
    // For other roles, only return valid organization IDs (not synthetic)
    if (currentOrganization?.id && currentOrganization.id !== 'synthetic-org') {
      return currentOrganization.id;
    }
    return undefined;
  };

  // Check if organization selection is required but not selected
  // service_admin belongs here with the other two. getOrganizationId() already
  // treats all three the same — it returns undefined until an organisation is
  // picked — but only master and super admins were ever ASKED to pick one. A
  // service provider therefore got no prompt and no organisationId, and the
  // backend's no-organisation path reads the LOCAL call tables rather than
  // federating to the switches, which is where this estate's calls actually
  // live. The page came back empty and said nothing.
  const needsOrgSelection = FEATURE_FLAGS.REQUIRE_ORG_SELECTION_FOR_OPERATIONAL_DATA &&
                            (currentRole === 'master_admin' || currentRole === 'super_admin' || currentRole === 'service_admin') &&
                            (!selectedOrgId || selectedOrgId === 'all');

  const { data, loading, error, refetch } = useApi<PaginatedResponse<CallTransfer>>(
    () => {
      // Skip API call if organization selection is required but not selected
      if (needsOrgSelection) {
        return Promise.resolve({ success: true, data: { data: [], total: 0, totalPages: 0, currentPage: 1 } });
      }
      return transfersService.getTransfers({ ...filters, page: currentPage, organizationId: getOrganizationId() });
    },
    // filters.callId belongs here. The request already spreads ...filters, so
    // the value WAS being sent — but useApi never re-fired when it changed, so
    // typing a Call ID updated state and nothing happened. The control looked
    // alive and did nothing, which reads as "search is broken" rather than as a
    // missing dependency.
    [currentPage, filters.transferType, filters.status, filters.dateFrom, filters.dateTo, filters.callerId, filters.callId, selectedOrgId, currentOrganization?.id, needsOrgSelection]
  );

  const handleFilterChange = (newFilters: FilterParams) => {
    setFilters(newFilters);
    setCurrentPage(1); // Reset to first page when filters change
  };

  // Fetch call log when modal opens
  useEffect(() => {
    // No original call id means there is no call to look up. The federated rows
    // always carry one, but the type now admits null and asking the API for
    // `undefined` would 400 rather than simply show no recording.
    if (isModalOpen && selectedTransfer?.originalCallId) {
      const originalCallId = selectedTransfer.originalCallId;
      const fetchCallLog = async () => {
        setLoadingCallLog(true);
        try {
          // Try to get the original call first
          const response = await callsService.getCallByUniqueId(
            originalCallId,
            getOrganizationId()
          );

          if (response.success && response.data) {
            setCallLog(response.data);
          }
        } catch (error) {
          console.error('Failed to fetch call log:', error);
        } finally {
          setLoadingCallLog(false);
        }
      };

      fetchCallLog();
    } else {
      setCallLog(null);
      setShowPlayer(false);
    }
  }, [isModalOpen, selectedTransfer]);

  const handlePlayRecording = () => {
    if (!callLog || !callLog.recording_url) return;

    try {
      const url = recordingsService.getAuthenticatedRecordingUrl(callLog.recording_url);
      setAuthenticatedUrl(url);
      setShowPlayer(true);
    } catch (error) {
      console.error('Play failed:', error);
    }
  };

  const handleDownloadRecording = async () => {
    // Resolved the same way playback is, rather than by appending the session
    // token to the stored URL: on this estate that URL names
    // recordings.greengiraffe.in, which wants the short-lived token cpass mints
    // as `?t=` and treats `?token=` as no token at all.
    if (!callLog) return;

    try {
      const callId = (callLog as any)?.call_id || callLog.unique_id;
      const url = await recordingsService.resolveDownloadUrl(
        callId, callLog.recording_url, getOrganizationId());
      if (!url) return;
      // .wav, not .mp3 — that is what the switch writes and the CDN serves.
      recordingsService.startDownload(url, `recording-${callId}.wav`);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  /**
   * Whether the org selector can fix this error — so keep it on screen.
   *
   * This used to test `error.includes('Organization not found')`, matching the
   * server's exact sentence. When that message was corrected to name its real
   * cause, the match stopped firing and the page rendered the bare error with
   * the dropdown REMOVED — telling the reader to select a business while hiding
   * the control that selects one.
   *
   * A page whose only input is the organization picker should never hide the
   * picker because a fetch failed: that strands the reader with no way to change
   * the input that caused it. So the condition is "an org is selectable here",
   * not "the server said a particular sentence", and nothing on this page is
   * coupled to server wording any more.
   */
  const canPickOrganization =
    currentRole === 'master_admin' || currentRole === 'super_admin' || currentRole === 'service_admin';
  const errorIsFixableByPickingAnOrg = Boolean(error) && canPickOrganization;

  if (loading) return <LoadingSpinner />;
  if (error && !errorIsFixableByPickingAnOrg) return <ErrorMessage message={error} onRetry={refetch} />;
  if (!data && !errorIsFixableByPickingAnOrg) return null;

  return (
    <div className="space-y-3 lg:space-y-4">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
        <h1 className="text-xl lg:text-2xl font-bold text-gray-800 dark:text-white">
          Call Transfers {data ? `(${data.total})` : ''}
        </h1>
        <button
          onClick={refetch}
          className="self-start sm:self-auto px-3 lg:px-4 py-1.5 lg:py-2 text-xs lg:text-sm text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition"
        >
          Refresh
        </button>
      </div>

      {/* Organization Filter - For master_admin, super_admin, and service_admin */}
      {(currentRole === 'master_admin' || currentRole === 'super_admin' || currentRole === 'service_admin') && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-3 lg:p-4">
          <OrganizationFilter
            value={selectedOrgId === 'all' ? '' : selectedOrgId}
            onChange={(value) => {
              // Feature Flag: When REQUIRE_ORG_SELECTION is enabled, don't allow 'all'
              if (FEATURE_FLAGS.REQUIRE_ORG_SELECTION_FOR_OPERATIONAL_DATA) {
                setSelectedOrgId(value || selectedOrgId); // Keep current if empty
              } else {
                setSelectedOrgId(value || 'all'); // Legacy: allow 'all'
              }
              setCurrentPage(1);
            }}
            className="w-full lg:w-96"
            requireSelection={FEATURE_FLAGS.REQUIRE_ORG_SELECTION_FOR_OPERATIONAL_DATA}
            allowServiceProviderSelection={true}
          />
        </div>
      )}

      {/* Show friendly message when organization selection is required */}
      {needsOrgSelection && (
        <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4 lg:p-6">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0">
              <Building2 className="w-6 h-6 lg:w-8 lg:h-8 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-base lg:text-lg font-semibold text-blue-900 dark:text-blue-200">Select an Organization</h3>
              <p className="mt-1 text-xs lg:text-sm text-blue-700 dark:text-blue-300">
                Please select an organization from the dropdown above to view call transfers.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Show error message if organization not found, but keep filters visible */}
      {!needsOrgSelection && errorIsFixableByPickingAnOrg && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-3 lg:p-4">
          <div className="flex items-start gap-2">
            <div className="flex-shrink-0">
              <svg className="w-4 h-4 lg:w-5 lg:h-5 text-red-600 dark:text-red-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-xs lg:text-sm font-medium text-red-800 dark:text-red-200">No transfers for this organization</h3>
              <p className="mt-1 text-xs lg:text-sm text-red-700 dark:text-red-300">{error}</p>
              <p className="mt-1 text-xs lg:text-sm text-red-600 dark:text-red-400">Select a different organization from the dropdown above.</p>
            </div>
          </div>
        </div>
      )}

      {/* Filters - Only show if we have data or it's not an org error */}
      {!needsOrgSelection && !errorIsFixableByPickingAnOrg && <TransfersFilterBar filters={filters} onFilterChange={handleFilterChange} />}

      {!needsOrgSelection && !errorIsFixableByPickingAnOrg && data && <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        {/* Mobile Card View */}
        <div className="lg:hidden divide-y divide-gray-200 dark:divide-gray-700">
          {data.data.map((transfer) => (
            <div
              key={transfer.id}
              onClick={() => {
                setSelectedTransfer(transfer);
                setIsModalOpen(true);
              }}
              className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                    transfer.transferType === 'ai'
                      ? 'bg-purple-100 dark:bg-purple-900/40 text-purple-800 dark:text-purple-300'
                      : 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300'
                  }`}>
                    {transfer.transferType === 'ai' ? (
                      <Bot className="w-3 h-3" />
                    ) : (
                      <Hash className="w-3 h-3" />
                    )}
                    {typeLabel(transfer.transferType)}
                  </span>
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                    isDone(transfer.transferStatus)
                      ? 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300'
                      : 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-300'
                  }`}>
                    {isDone(transfer.transferStatus) ? (
                      <CheckCircle className="w-3 h-3" />
                    ) : (
                      <Clock className="w-3 h-3" />
                    )}
                    {transfer.transferStatus || '—'}
                  </span>
                </div>
                <Eye className="w-4 h-4 text-gray-400" />
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <Phone className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                  <span className="text-sm font-medium text-gray-900 dark:text-white truncate">{transfer.callerId}</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                  <PhoneForwarded className="w-3.5 h-3.5 flex-shrink-0" />
                  <span className="truncate">{transfer.transferredFrom || '—'} → {transfer.transferredTo || '—'}</span>
                </div>
                <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                  <span>TRANS-{transfer.id}</span>
                  <span>{formatTimestamp(transfer.transferTime)}</span>
                </div>
              </div>
            </div>
          ))}

          {/* Mobile Empty State */}
          {data.data.length === 0 && (
            <div className="p-8 text-center">
              <PhoneForwarded className="w-10 h-10 text-gray-400 mx-auto mb-3" />
              <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-1">No Transfers Found</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {(currentRole === 'master_admin' || currentRole === 'super_admin')
                  ? 'No transfers for this business. Try different filters.'
                  : 'Try adjusting your date range or filters.'}
              </p>
            </div>
          )}
        </div>

        {/* Desktop Table View */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Transfer ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Caller ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Transferred From
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Transferred To
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Transfer Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Transfer Time
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {data.data.map((transfer) => (
                <tr key={transfer.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                    TRANS-{transfer.id}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                    <div className="flex items-center gap-1">
                      <Phone className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                      {transfer.callerId}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                    {transfer.transferredFrom}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                    {transfer.transferredTo || '—'}
                  </td>
                  <td className="px-6 py-4 text-sm">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                      transfer.transferType === 'ai'
                        ? 'bg-purple-100 dark:bg-purple-900/40 text-purple-800 dark:text-purple-300'
                        : 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300'
                    }`}>
                      {transfer.transferType === 'ai' ? (
                        <Bot className="w-3 h-3" />
                      ) : (
                        <Hash className="w-3 h-3" />
                      )}
                      {typeLabel(transfer.transferType)}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                      isDone(transfer.transferStatus)
                        ? 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300'
                        : 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-300'
                    }`}>
                      {isDone(transfer.transferStatus) ? (
                        <CheckCircle className="w-3 h-3" />
                      ) : (
                        <Clock className="w-3 h-3" />
                      )}
                      {transfer.transferStatus || '—'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                    {formatTimestamp(transfer.transferTime)}
                  </td>
                  <td className="px-6 py-4 text-sm">
                    <button
                      onClick={() => {
                        setSelectedTransfer(transfer);
                        setIsModalOpen(true);
                      }}
                      className="inline-flex items-center gap-1 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline"
                    >
                      <Eye className="w-4 h-4" />
                      View Details
                    </button>
                  </td>
                </tr>
              ))}

              {/* Desktop Empty state */}
              {data.data.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-6 py-16 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <PhoneForwarded className="w-12 h-12 text-gray-400" />
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">No Call Transfers Found</h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {(currentRole === 'master_admin' || currentRole === 'super_admin')
                            ? 'No call transfers found for the selected business and filters. Try selecting a different business or adjusting your date range.'
                            : 'No call transfers found for the selected filters. Try adjusting your date range or filters.'}
                        </p>
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {data.data.length > 0 && <div className="p-3 lg:p-4 border-t dark:border-gray-700">
          <Pagination
            currentPage={currentPage}
            totalPages={data.totalPages}
            onPageChange={setCurrentPage}
            totalItems={data.total}
            itemsPerPage={ITEMS_PER_PAGE}
          />
        </div>}
      </div>}

      {/* Transfer Detail Modal */}
      {isModalOpen && selectedTransfer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-2 lg:p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 lg:px-6 py-3 lg:py-4 flex justify-between items-center">
              <h2 className="text-lg lg:text-xl font-bold text-gray-900 dark:text-white">Transfer Details</h2>
              <button
                onClick={() => {
                  setIsModalOpen(false);
                  setSelectedTransfer(null);
                }}
                className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition"
              >
                <svg className="w-5 h-5 lg:w-6 lg:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="px-4 lg:px-6 py-4 space-y-4 lg:space-y-6">
              {/* Transfer Summary Section */}
              {selectedTransfer.transferCallSummary && (
                <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg p-3 lg:p-4">
                  <h3 className="text-xs lg:text-sm font-semibold text-blue-900 dark:text-blue-200 mb-2 flex items-center gap-2">
                    <Bot className="w-3.5 h-3.5 lg:w-4 lg:h-4" />
                    Transfer Summary
                  </h3>
                  <p className="text-xs lg:text-sm text-blue-800 dark:text-blue-300">{maskSensitiveNumbers(selectedTransfer.transferCallSummary)}</p>
                </div>
              )}

              {/* Basic Information */}
              <div>
                <h3 className="text-xs lg:text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 lg:mb-3">Basic Information</h3>
                <div className="grid grid-cols-2 gap-3 lg:gap-4">
                  <div>
                    <label className="text-xs text-gray-500 dark:text-gray-400 uppercase">Transfer ID</label>
                    <p className="text-xs lg:text-sm font-medium text-gray-900 dark:text-white">TRANS-{selectedTransfer.id}</p>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 dark:text-gray-400 uppercase">Business ID</label>
                    <p className="text-xs lg:text-sm font-medium text-gray-900 dark:text-white truncate">{selectedTransfer.businessId}</p>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 dark:text-gray-400 uppercase">Caller ID</label>
                    <p className="text-xs lg:text-sm font-medium text-gray-900 dark:text-white flex items-center gap-1">
                      <Phone className="w-3 h-3 text-gray-500 dark:text-gray-400" />
                      {selectedTransfer.callerId}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 dark:text-gray-400 uppercase">Transfer Type</label>
                    <p className="text-xs lg:text-sm font-medium">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 lg:py-1 rounded-full text-xs font-medium ${
                        selectedTransfer.transferType === 'ai'
                          ? 'bg-purple-100 dark:bg-purple-900/40 text-purple-800 dark:text-purple-300'
                          : 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300'
                      }`}>
                        {selectedTransfer.transferType === 'ai' ? (
                          <Bot className="w-3 h-3" />
                        ) : (
                          <Hash className="w-3 h-3" />
                        )}
                        {typeLabel(selectedTransfer.transferType)}
                      </span>
                    </p>
                  </div>
                </div>
              </div>

              {/* Call Information */}
              <div>
                <h3 className="text-xs lg:text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 lg:mb-3">Call Information</h3>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 lg:gap-4">
                  <div>
                    <label className="text-xs text-gray-500 dark:text-gray-400 uppercase">Original Call ID</label>
                    <p className="text-xs lg:text-sm font-medium text-gray-900 dark:text-white font-mono truncate">{selectedTransfer.originalCallId}</p>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 dark:text-gray-400 uppercase">New Call ID</label>
                    <p className="text-xs lg:text-sm font-medium text-gray-900 dark:text-white font-mono truncate">{selectedTransfer.newCallId || '—'}</p>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 dark:text-gray-400 uppercase">Transferred From</label>
                    <p className="text-xs lg:text-sm font-medium text-gray-900 dark:text-white">{selectedTransfer.transferredFrom}</p>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 dark:text-gray-400 uppercase">Transferred To</label>
                    <p className="text-xs lg:text-sm font-medium text-gray-900 dark:text-white">{selectedTransfer.transferredTo}</p>
                  </div>
                </div>
              </div>

              {/* Status and Timing */}
              <div>
                <h3 className="text-xs lg:text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 lg:mb-3">Status & Timing</h3>
                <div className="grid grid-cols-2 gap-3 lg:gap-4">
                  <div>
                    <label className="text-xs text-gray-500 dark:text-gray-400 uppercase">Transfer Status</label>
                    <p className="text-xs lg:text-sm font-medium">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 lg:py-1 rounded-full text-xs font-medium ${
                        isDone(selectedTransfer.transferStatus)
                          ? 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300'
                          : 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-300'
                      }`}>
                        {isDone(selectedTransfer.transferStatus) ? (
                          <CheckCircle className="w-3 h-3" />
                        ) : (
                          <Clock className="w-3 h-3" />
                        )}
                        {selectedTransfer.transferStatus || '—'}
                      </span>
                    </p>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 dark:text-gray-400 uppercase">Transfer Time</label>
                    <p className="text-xs lg:text-sm font-medium text-gray-900 dark:text-white">{formatTimestamp(selectedTransfer.transferTime)}</p>
                  </div>
                  {selectedTransfer.completedTime && (
                    <div>
                      <label className="text-xs text-gray-500 dark:text-gray-400 uppercase">Completed Time</label>
                      <p className="text-xs lg:text-sm font-medium text-gray-900 dark:text-white">{formatTimestamp(selectedTransfer.completedTime)}</p>
                    </div>
                  )}
                  <div>
                    <label className="text-xs text-gray-500 dark:text-gray-400 uppercase">Created At</label>
                    <p className="text-xs lg:text-sm font-medium text-gray-900 dark:text-white">{formatTimestamp(selectedTransfer.createdAt)}</p>
                  </div>
                </div>
              </div>

              {/* Call Recording Section */}
              {loadingCallLog ? (
                <div className="flex justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 lg:h-8 lg:w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : callLog && callLog.recording_url ? (
                <div>
                  <h3 className="text-xs lg:text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 lg:mb-3 flex items-center gap-2">
                    <Headphones className="w-3.5 h-3.5 lg:w-4 lg:h-4" />
                    Call Recording
                  </h3>
                  <div className="bg-green-50 dark:bg-green-900/30 rounded-lg p-3 lg:p-4">
                    {!showPlayer ? (
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2 lg:gap-3 min-w-0">
                          <Headphones className="w-6 h-6 lg:w-8 lg:h-8 text-green-600 dark:text-green-400 flex-shrink-0" />
                          <div className="min-w-0">
                            <p className="font-medium text-xs lg:text-sm text-gray-900 dark:text-white">Recording Available</p>
                            <p className="text-xs text-gray-600 dark:text-gray-400 truncate">Call ID: {callLog.unique_id}</p>
                          </div>
                        </div>
                        <div className="flex gap-2 flex-shrink-0">
                          <button
                            onClick={handlePlayRecording}
                            className="p-1.5 lg:p-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                          >
                            <Play className="w-4 h-4 lg:w-5 lg:h-5" />
                          </button>
                          <button
                            onClick={handleDownloadRecording}
                            className="p-1.5 lg:p-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500"
                          >
                            <Download className="w-4 h-4 lg:w-5 lg:h-5" />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-2 lg:space-y-3">
                        <div className="flex items-center justify-between">
                          <p className="font-medium text-xs lg:text-sm text-gray-900 dark:text-white">Now Playing</p>
                          <button
                            onClick={() => setShowPlayer(false)}
                            className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                          >
                            <XCircle className="w-4 h-4 lg:w-5 lg:h-5" />
                          </button>
                        </div>
                        <AudioPlayer src={authenticatedUrl} />
                        <div className="flex justify-end">
                          <button
                            onClick={handleDownloadRecording}
                            className="flex items-center gap-1.5 lg:gap-2 px-2 lg:px-3 py-1 lg:py-1.5 text-xs lg:text-sm bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500"
                          >
                            <Download className="w-3.5 h-3.5 lg:w-4 lg:h-4" />
                            Download
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : null}
            </div>

            <div className="sticky bottom-0 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600 px-4 lg:px-6 py-3 lg:py-4 flex justify-end">
              <button
                onClick={() => {
                  setIsModalOpen(false);
                  setSelectedTransfer(null);
                }}
                className="px-3 lg:px-4 py-1.5 lg:py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Transfers;